import { useEffect, useRef } from 'react';
import * as THREE from 'three';

/** Soft field of tiny particles that undulate like a wave. */
export function AuthParticles() {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mountElement = mountRef.current;
    if (!mountElement) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 1000);
    camera.position.set(0, 80, 20);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);
    mountElement.appendChild(renderer.domElement);

    const particleCount = 5000;
    const boundsX = 100;
    const boundsZ = 100;

    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const baseX = new Float32Array(particleCount);
    const baseZ = new Float32Array(particleCount);
    const phases = new Float32Array(particleCount);
    const colors = new Float32Array(particleCount * 3);

    const colorA = new THREE.Color(0x70f7ff);
    const colorB = new THREE.Color(0x9b7cff);

    for (let i = 0; i < particleCount; i += 1) {
      const i3 = i * 3;
      const x = (Math.random() - 0.5) * boundsX * 2;
      const z = (Math.random() - 0.5) * boundsZ * 2;

      baseX[i] = x;
      baseZ[i] = z;
      phases[i] = Math.random() * Math.PI * 2;

      positions[i3] = x;
      positions[i3 + 1] = 0;
      positions[i3 + 2] = z;

      const mixed = colorA.clone().lerp(colorB, Math.random());
      colors[i3] = mixed.r;
      colors[i3 + 1] = mixed.g;
      colors[i3 + 2] = mixed.b;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.05,
      transparent: true,
      opacity: 0.72,
      sizeAttenuation: true,
      vertexColors: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const particles = new THREE.Points(geometry, material);
    scene.add(particles);

    const timer = new THREE.Timer();
    let frameId = 0;

    const resize = () => {
      const width = mountElement.clientWidth;
      const height = mountElement.clientHeight;
      if (width === 0 || height === 0) return;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
    };

    const updateWave = (elapsed: number) => {
      for (let i = 0; i < particleCount; i += 1) {
        const i3 = i * 3;
        const x = baseX[i];
        const z = baseZ[i];
        const phase = phases[i];

        const wave1 = Math.sin(x * 0.085 + elapsed * 0.85 + phase) * 4.2;
        const wave2 = Math.cos(z * 0.11 + elapsed * 0.55) * 3.1;
        const wave3 = Math.sin((x + z) * 0.05 + elapsed * 0.4) * 1.8;

        positions[i3] = x + Math.sin(elapsed * 0.3 + z * 0.04) * 0.35;
        positions[i3 + 1] = wave1 + wave2 + wave3;
        positions[i3 + 2] = z;
      }

      geometry.attributes.position.needsUpdate = true;
    };

    const animate = (timestamp?: number) => {
      timer.update(timestamp ?? performance.now());
      const elapsed = timer.getElapsed();
      updateWave(elapsed);
      particles.rotation.y = Math.sin(elapsed * 0.08) * 0.08;
      renderer.render(scene, camera);
      frameId = window.requestAnimationFrame(animate);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mountElement);
    window.addEventListener('resize', resize);
    resize();
    animate();

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
      resizeObserver.disconnect();
      if (renderer.domElement.parentElement === mountElement) {
        mountElement.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  return <div ref={mountRef} className="auth-three-background" aria-hidden="true" />;
}
